import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, AlertTitle, Stack, Link, Chip,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import VolverInicio from '../components/VolverInicio';
import { leerError } from '../utils/errores';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type Campo = 'nombre' | 'email' | 'password';

const Registro: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ nombre: '', email: '', password: '', empresa: '', telefono: '' });
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [campos, setCampos] = useState<Partial<Record<Campo, string>>>({});
  const [loading, setLoading] = useState(false);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [k]: e.target.value });
    if (campos[k as Campo]) setCampos({ ...campos, [k]: undefined });
  };

  const validar = (): boolean => {
    const c: Partial<Record<Campo, string>> = {};
    if (!form.nombre.trim()) c.nombre = 'Ingresa tu nombre completo.';
    if (!form.email.trim()) c.email = 'Ingresa tu email.';
    else if (!EMAIL_RE.test(form.email.trim())) c.email = 'Revisa el formato del email (ej: nombre@empresa.cl).';
    if (form.password.length < 6) c.password = 'La contraseña debe tener al menos 6 caracteres.';
    setCampos(c);
    return Object.keys(c).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    if (!validar()) return;
    setLoading(true);
    try {
      await register({ ...form, email: form.email.trim() });
      navigate('/app');
    } catch (err: any) {
      const { message, code, field } = leerError(err, 'No pudimos crear tu cuenta. Intenta de nuevo.');
      setError(message);
      setErrorCode(code || '');
      if (field === 'nombre' || field === 'email' || field === 'password') setCampos({ [field]: ' ' });
    } finally {
      setLoading(false);
    }
  };

  const emailParam = encodeURIComponent(form.email.trim());

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #075E54, #25D366)' }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: 4 }}>
          <VolverInicio />
          <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Logo size={44} />
            <Typography variant="h5" sx={{ pt: 1 }}>Crea tu cuenta</Typography>
            <Chip label="7 días gratis · sin tarjeta" color="success" size="small" />
          </Stack>

          {error && errorCode === 'EMAIL_EXISTE' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Este email ya tiene una cuenta</AlertTitle>
              {error} Puedes{' '}
              <Link component={RouterLink} to={`/login?email=${emailParam}`} fontWeight={600}>iniciar sesión</Link>
              {' '}o, si no recuerdas tu contraseña,{' '}
              <Link component={RouterLink} to={`/recuperar-contrasena?email=${emailParam}`} fontWeight={600}>recuperarla aquí</Link>.
            </Alert>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : null}

          <form onSubmit={submit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Nombre completo" fullWidth required value={form.nombre} onChange={set('nombre')}
                error={!!campos.nombre} helperText={campos.nombre?.trim() || undefined}
              />
              <TextField
                label="Email" type="email" fullWidth required value={form.email} onChange={set('email')}
                error={!!campos.email} helperText={campos.email?.trim() || undefined}
              />
              <TextField
                label="Contraseña" type="password" fullWidth required value={form.password} onChange={set('password')}
                error={!!campos.password} helperText={campos.password?.trim() || 'Mínimo 6 caracteres'}
              />
              <TextField label="Empresa (opcional)" fullWidth value={form.empresa} onChange={set('empresa')} />
              <TextField label="Teléfono (opcional)" fullWidth value={form.telefono} onChange={set('telefono')} />
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? 'Creando tu cuenta...' : 'Comenzar gratis'}
              </Button>
            </Stack>
          </form>
          <Typography variant="body2" textAlign="center" sx={{ mt: 3 }}>
            ¿Ya tienes cuenta? <Link component={RouterLink} to="/login">Inicia sesión</Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Registro;
